import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { Typography, useMediaQuery, useTheme } from '@mui/material';

import { ComparisonKeys } from './ComparisonKeys';
import { ComparisonTable } from './ComparisonTable';
import { BenefitPlanDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { PsButtonTuner } from 'stencilSetup';
import {
  ComparisonSummaryContainerStyled,
  DetailedComparisonContentStyled,
  DetailedComparisonLabel,
  DetailedComparisonStyled,
  KeyDifferencesLabelStyled,
  KeyDifferencesStyled,
  OtherDifferencesLabelStyled,
} from './ComparePlansStyled';
import './ComparisonTable.css';

export interface ComparisonSummaryProps {
  plansDetails: any;
  currentPlan?: BenefitPlanDto;
  comparisonKeys?: string[];
  getComparisonKeys?: (keys: string[]) => void;
}

export const ComparisonSummary = ({
  getComparisonKeys,
  comparisonKeys,
  plansDetails,
  currentPlan,
}: ComparisonSummaryProps) => {
  const { t } = useTranslation('pages.enrollmentComparePlans');

  const [planDetailsIds, setPlanDetailsIds] = useState<string[]>([]);
  const [contentDetails, setContentDetails] = useState<any>([]);
  const [uniqueLabels, setUniqueLabels] = useState<any>([]);
  const theme = useTheme();
  const isScreenSmall = useMediaQuery(theme.breakpoints.down(1200));

  // TODO: move this to utils
  const sortByKey = (array: any[], key: string) => {
    return array.sort(function (a, b) {
      const x = a[key];
      const y = b[key];
      return x < y ? -1 : x > y ? 1 : 0;
    });
  };

  useEffect(() => {
    const sortContentDetails = plansDetails?.map((plan: BenefitPlanDto) => {
      return sortByKey(plan.content_details, 'label');
    });

    const allContentDetails = Array.prototype.concat(...sortContentDetails);

    const sortedContentValues = sortByKey(allContentDetails, 'label');

    // get only unique labels
    const getLabels = sortedContentValues?.map((content: any) => {
      return content.label;
    });

    setUniqueLabels([...new Set(getLabels)]);

    // get plan_id of comparison plans
    const planIds = plansDetails?.map((plan: BenefitPlanDto) => {
      return plan.plan_id;
    });

    // create an object with plan_id as key and content details as value
    let planContentDetails: any = [];
    plansDetails.forEach((plan: BenefitPlanDto) => {
      const planId = plan.plan_id;
      planContentDetails = {
        ...planContentDetails,
        [planId]: plan.content_details,
      };
    });

    const planContentDetailsObject: any = {};
    planIds.forEach((planId: string) => {
      planContentDetailsObject[planId] = {};
      planContentDetails[planId].forEach((content: any) => {
        planContentDetailsObject[planId][content.label] = {
          inNetwork: content.in_network === null ? 'NA' : content.in_network,
          outNetwork:
            content.out_of_network === null ? 'NA' : content.out_of_network,
          preferredNetwork:
            content.preferred_network === null
              ? 'NA'
              : content.preferred_network,
        };
      });
    });

    setPlanDetailsIds(planIds);
    setContentDetails(planContentDetailsObject);
  }, [plansDetails]);

  const [showDetailedComparison, setShowDetailedComparison] =
    useState<boolean>(false);

  const handleDetailedComparison = () => {
    setShowDetailedComparison(!showDetailedComparison);
  };

  const renderKeyDifferences = () => {
    return uniqueLabels
      .slice(0, 3)
      ?.map((label: string, index: number) => (
        <ComparisonTable
          key={index}
          label={label}
          planContentDetails={contentDetails}
          comparisonKeys={comparisonKeys}
          planDetailsIds={planDetailsIds}
          currentPlan={currentPlan}
        />
      ));
  };

  const renderComparisonHeader = () => {
    return (
      <div>
        <h3 className="headline-3 bold mb-0">{t('comparisonSummary')}</h3>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <KeyDifferencesLabelStyled>
            3 {t('keyDifferences')}
          </KeyDifferencesLabelStyled>
          {!isScreenSmall && (
            <>
              <ComparisonKeys setComparisonKeys={getComparisonKeys} />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <ComparisonSummaryContainerStyled className="comparison-summary">
      {!isScreenSmall ? (
        renderComparisonHeader()
      ) : (
        <div>
          {Object.keys(contentDetails)?.map((key: any) =>
            currentPlan?.plan_id?.toString() === key
              ? renderComparisonHeader()
              : null
          )}
        </div>
      )}

      {isScreenSmall ? (
        <KeyDifferencesStyled>{renderKeyDifferences()}</KeyDifferencesStyled>
      ) : (
        <div className="comparison-table">
          <table className="w-100 ">
            <thead className="sr-only">
              <tr>
                {planDetailsIds.map((id) => {
                  const plan = plansDetails.find(
                    (p: BenefitPlanDto) => p.plan_id === id
                  );
                  return (
                    <th key={id} scope="col">
                      {plan?.plan_name}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {uniqueLabels.slice(0, 3).map((label: string, i: number) => (
              <React.Fragment key={i}>
                <tbody>
                  <tr>
                    <td
                      colSpan={planDetailsIds.length}
                      className="border-bottom details"
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {label}
                      </Typography>
                    </td>
                  </tr>
                  <tr>
                    {planDetailsIds.map((planId, j) => {
                      const valueSet = contentDetails?.[planId]?.[label];
                      return (
                        <td key={j}>
                          <div className="equal-width">
                            {comparisonKeys?.includes('In-networks') && (
                              <div className="prices">
                                <Typography variant="body2">
                                  {valueSet?.inNetwork ?? 'NA'}
                                </Typography>
                                <div>
                                  <Typography fontSize={10}>
                                    {t('InNetworkCaps')}
                                  </Typography>
                                </div>
                              </div>
                            )}
                            {comparisonKeys?.includes('Out-networks') && (
                              <div className="prices">
                                <Typography variant="body2">
                                  {valueSet?.outNetwork ?? 'NA'}
                                </Typography>
                                <div>
                                  <Typography fontSize={10}>
                                    {t('OutOfNetworkCaps')}
                                  </Typography>
                                </div>
                              </div>
                            )}
                            {comparisonKeys?.includes('Preferred-networks') && (
                              <div className="prices">
                                <Typography variant="body2">
                                  {valueSet?.preferredNetwork ?? 'NA'}
                                </Typography>
                                <div>
                                  <Typography fontSize={10}>
                                    {t('PreferredNetworkCaps')}
                                  </Typography>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
                <tr
                  className="visual-gap"
                  aria-hidden="true"
                  role="presentation"
                >
                  {planDetailsIds.map((planId, j) => {
                    return <td key={j}></td>;
                  })}
                </tr>
              </React.Fragment>
            ))}
          </table>
        </div>
      )}

      {!isScreenSmall && showDetailedComparison && (
        <div className=" mt-5">
          <h3 className="headline-3 bold mb-0 px-lg-4">
            {t('detailedComparison')}
          </h3>
          <Typography component="div" variant="body1" className="mb-4">
            {uniqueLabels.slice(3).length} {t('differences')}
          </Typography>
          <div className="comparison-table">
            <table className="w-100">
              <thead className="sr-only">
                <tr>
                  {planDetailsIds.map((id) => {
                    const plan = plansDetails.find(
                      (p: BenefitPlanDto) => p.plan_id === id
                    );
                    return (
                      <th key={id} scope="col">
                        {plan?.plan_name}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {uniqueLabels.slice(3).map((label: string, i: number) => (
                <tbody key={i}>
                  <tr>
                    <td
                      colSpan={planDetailsIds.length}
                      className="border-bottom"
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {label}
                      </Typography>
                    </td>
                  </tr>
                  <tr>
                    {planDetailsIds.map((planId, j) => {
                      const valueSet = contentDetails?.[planId]?.[label];
                      return (
                        <td key={j}>
                          <div className="equal-width">
                            {comparisonKeys?.includes('In-networks') && (
                              <div className="prices">
                                <Typography variant="body2">
                                  {valueSet?.inNetwork ?? 'NA'}
                                </Typography>
                                <div>
                                  <Typography fontSize={10}>
                                    {t('InNetworkCaps')}
                                  </Typography>
                                </div>
                              </div>
                            )}
                            {comparisonKeys?.includes('Out-networks') && (
                              <div className="prices">
                                <Typography variant="body2">
                                  {valueSet?.outNetwork ?? 'NA'}
                                </Typography>
                                <div>
                                  <Typography fontSize={10}>
                                    {t('OutOfNetworkCaps')}
                                  </Typography>
                                </div>
                              </div>
                            )}
                            {comparisonKeys?.includes('Preferred-networks') && (
                              <div className="prices">
                                <Typography variant="body2">
                                  {valueSet?.preferredNetwork ?? 'NA'}
                                </Typography>
                                <div>
                                  <Typography fontSize={10}>
                                    {t('PreferredNetworkCaps')}
                                  </Typography>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              ))}
            </table>
          </div>
        </div>
      )}

      {isScreenSmall ? (
        <div>
          {Object.keys(contentDetails)?.map(
            (key: any, index: number) =>
              currentPlan?.plan_id?.toString() === key && (
                <div key={index}>
                  <DetailedComparisonStyled onClick={handleDetailedComparison}>
                    <div className="d-flex flex-column">
                      <DetailedComparisonLabel>
                        {t('detailedComparison')}
                      </DetailedComparisonLabel>
                      <OtherDifferencesLabelStyled>
                        {uniqueLabels.slice(3).length} {t('differences')}
                      </OtherDifferencesLabelStyled>
                    </div>
                    {showDetailedComparison ? (
                      <CaretUp></CaretUp>
                    ) : (
                      <CaretDown></CaretDown>
                    )}
                  </DetailedComparisonStyled>
                </div>
              )
          )}
        </div>
      ) : (
        <div className="d-flex my-3 justify-content-center">
          <PsButtonTuner
            buttonId="detailed-comparison-button"
            label={t('detailedComparison')}
            iconRight={!showDetailedComparison ? 'caret-down' : 'caret-up'}
            level="primary"
            onClick={handleDetailedComparison}
          />
        </div>
      )}
      {showDetailedComparison && isScreenSmall && (
        <DetailedComparisonContentStyled>
          {uniqueLabels
            .slice(3)
            ?.map((label: string, index: number) => (
              <ComparisonTable
                key={index}
                label={label}
                planContentDetails={contentDetails}
                comparisonKeys={comparisonKeys}
                planDetailsIds={planDetailsIds}
                currentPlan={currentPlan}
              />
            ))}
        </DetailedComparisonContentStyled>
      )}
    </ComparisonSummaryContainerStyled>
  );
};
